import React, { useState, useEffect, useRef } from "react";
import { ScanLine, Loader2, Camera, X } from "lucide-react";

export const PUBLIC_STEPS = [
  "Order received", "Items being prepared", "Order packed",
  "Shipped from the UK", "Arrived in Ghana", "Out for delivery", "Delivered",
];

export const STEP_TONE = [
  "bg-black/10 text-[#5C5C5C]", "bg-[#D9933B]/15 text-[#B5771F]",
  "bg-[#306D29]/15 text-[#306D29]", "bg-[#2563EB]/15 text-[#2563EB]",
  "bg-[#7C3AED]/15 text-[#7C3AED]", "bg-[#0891B2]/15 text-[#0891B2]",
  "bg-[#2D7A5D] text-white",
];

export const PICK_STATES = [
  { id: "picked", label: "Picked", tone: "bg-[#306D29] text-white" },
  { id: "partial", label: "Partial", tone: "bg-[#D9933B] text-white" },
  { id: "substitute", label: "Substitute", tone: "bg-[#2563EB] text-white" },
  { id: "not_found", label: "Not found", tone: "bg-[#B23B3B] text-white" },
  { id: "wrong_item", label: "Wrong item", tone: "bg-[#B23B3B] text-white" },
  { id: "damaged", label: "Damaged", tone: "bg-[#B23B3B] text-white" },
  { id: "on_hold", label: "On hold", tone: "bg-black/50 text-white" },
];

export const EXCEPTION_STATES = ["not_found", "wrong_item", "damaged", "on_hold"];

export const EXCEPTION_REASONS = [
  "Item not found", "Only partial quantity available", "Damaged item",
  "Wrong variant or size", "Retailer out of stock", "Substitute available",
  "Customer decision needed", "Manager approval needed",
];

export function StepPill({ step, className = "" }) {
  const s = typeof step === "number" ? step : 0;
  return (
    <span data-testid="step-pill" className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${STEP_TONE[s] || STEP_TONE[0]} ${className}`}>
      {PUBLIC_STEPS[s]}
    </span>
  );
}

export function PickPill({ status }) {
  const p = PICK_STATES.find((x) => x.id === status);
  if (!p) return <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-black/5 text-[#5C5C5C]">Pending</span>;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${p.tone}`}>{p.label}</span>;
}

export function Card({ children, className = "" }) {
  return <div className={`bg-white border border-black/5 rounded-2xl p-5 ${className}`}>{children}</div>;
}

export function EmptyState({ children }) {
  return <div className="text-center text-[#5C5C5C] py-16 text-sm" data-testid="fx-empty">{children}</div>;
}

// Scan / search bar shared across modules (type or scan with phone camera)
export function ScanBar({ onSubmit, placeholder = "Scan or type a code…", testid = "fx-scan" }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [cam, setCam] = useState(false);
  const go = async () => {
    if (!val.trim()) return;
    setBusy(true);
    try { await onSubmit(val.trim()); setVal(""); } finally { setBusy(false); }
  };
  const onCam = async (text) => {
    setCam(false);
    let code = text;
    if (code.includes("/s/")) code = code.split("/s/")[1].split("?")[0].replace(/\//g, "");
    await onSubmit(code.trim());
  };
  return (
    <div className="flex gap-2 mb-6">
      <div className="relative flex-1">
        <ScanLine size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#306D29]" />
        <input data-testid={`${testid}-input`} autoFocus value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && go()} placeholder={placeholder}
          className="w-full bg-white border border-black/10 rounded-full pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-[#306D29] focus:outline-none" />
      </div>
      <button data-testid={`${testid}-camera`} onClick={() => setCam(true)} title="Scan with camera"
        className="bg-white border border-black/10 text-[#306D29] px-4 rounded-full flex items-center hover:border-[#306D29]/40"><Camera size={18} /></button>
      <button data-testid={`${testid}-btn`} onClick={go} disabled={busy}
        className="bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-5 rounded-full flex items-center gap-2 disabled:opacity-60"
        style={{ transition: "background-color 0.2s ease" }}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />} Open
      </button>
      {cam && <CameraScanModal onClose={() => setCam(false)} onResult={onCam} />}
    </div>
  );
}

function CameraScanModal({ onClose, onResult }) {
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!active) return;
        const scanner = new Html5Qrcode("fx-qr-reader");
        scannerRef.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
          (text) => { onResult(text); }, () => {});
      } catch (e) { setError("Could not start camera. Check permissions or type the code instead."); }
    })();
    return () => {
      const s = scannerRef.current;
      if (s) { s.stop().then(() => s.clear()).catch(() => {}); scannerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FBF5DD] rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="fx-camera-modal">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl text-[#0D530E]">Scan a QR label</h3>
          <button onClick={onClose} data-testid="fx-camera-close"><X size={20} /></button>
        </div>
        <div id="fx-qr-reader" className="rounded-2xl overflow-hidden w-full" />
        {error && <p className="text-sm text-[#B23B3B] mt-3">{error}</p>}
        <p className="text-xs text-[#5C5C5C] mt-3">Point the camera at an order, package or container QR.</p>
      </div>
    </div>
  );
}
